import {Logger} from '@vanti/vue-logger';
import {bookings as impl} from '@/room-booking/impl';
import {toDate} from '@/utils/dates';

const log = Logger.get('auto-check-out');

/**
 * @param {Booking} booking
 * @param {string} reservationKey
 * @return {boolean}
 */
function isCheckedIn(booking, reservationKey) {
  if (!booking || !impl.requiresCheckIn(booking, reservationKey)) {
    return true;
  }
  const checkInPeriod = impl.checkInPeriod(booking, reservationKey);
  return checkInPeriod && checkInPeriod.hasOwnProperty('fromTime');
}
/**
 * @param {Booking} booking
 * @param {string} reservationKey
 * @return {boolean}
 */
function isCheckedOut(booking, reservationKey) {
  if (!booking || !impl.requiresCheckIn(booking, reservationKey)) {
    return true;
  }
  const checkInPeriod = impl.checkInPeriod(booking, reservationKey);
  return checkInPeriod && checkInPeriod.hasOwnProperty('toTime');
}

/**
 * @param {Booking} booking
 * @param {string} reservationKey
 * @param {number} checkOutMins
 * @return {{msFromNow: number, checkOutAt: Date}}
 */
function autoCheckOutCalc(booking, reservationKey, checkOutMins) {
  const start = toDate(impl.fromTime(booking, reservationKey));
  const now = new Date();
  const checkOutAt = new Date(start.valueOf() + checkOutMins * 60 * 1000);
  const msFromNow = checkOutAt.valueOf() - now.valueOf();
  return {checkOutAt, msFromNow};
}

export default {
  namespaced: true,
  state: {
    initialised: false
  },
  mutations: {
    setInitialised(state, i) {
      state.initialised = i;
    }
  },
  actions: {
    init: {
      root: true,
      handler({rootGetters, commit, state, dispatch}) {
        if (state.initialised) {
          return; // only watch once
        }
        // sometimes hot-reload can cause init to be called twice, guard against that to prevent multiple watch calls
        commit('setInitialised', true);
        let timeout;
        this.watch(
            () => [
              rootGetters['config/autoCheckOut'],
              rootGetters['bookings/currentBooking'],
              rootGetters['bookings/currentReservationKey']
            ],
            ([checkOutMins, booking, reservationKey]) => {
              if (timeout) {
                clearTimeout(timeout);
              }
              if (typeof checkOutMins !== 'number') {
                return;
              }
              const checkedIn = isCheckedIn(booking, reservationKey);
              if (checkedIn) {
                return;
              }
              const {msFromNow, checkOutAt} = autoCheckOutCalc(booking, reservationKey, checkOutMins);
              log.debug(`scheduling auto check-out check at ${checkOutAt}`);

              timeout = setTimeout(() => {
                dispatch('runAutoCheckOut', checkOutMins);
              }, msFromNow);
            },
            {immediate: true}
        );
      }
    },
    runAutoCheckOut({rootGetters, dispatch}, checkOutMins) {
      log.debug('runAutoCheckOut');
      const booking = rootGetters['bookings/currentBooking'];
      const reservationKey = rootGetters['bookings/currentReservationKey'];
      // it shouldn't be the currentBooking if its checked-out, but just in case
      const checkedOut = isCheckedOut(booking, reservationKey);
      if (checkedOut) {
        return;
      }
      const {msFromNow} = autoCheckOutCalc(booking, reservationKey, checkOutMins);
      if (msFromNow <= 0) {
        log.debug('auto checking out');
        dispatch('bookings/releaseOrCancel', {release: true, booking}, {root: true});
      }
    }
  }
};
